import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Directive, ElementRef, Input, output, inject } from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type {
  PluginsEvents,
  IProsemirrorConfig,
  ConfigPlugins,
  PluginsAPI,
} from '@core/prosemirror-legacy';
import { EditorMode } from '@core/prosemirror-legacy';
import type { EditorJSONState } from '@core/prosemirror-types';

import { EditorView$, StateUpdated$ } from '../consts';
import { ProsemirrorService } from '../prosemirror.service';
import { OVERLAY_PROVIDERS } from '../providers';

@Directive({
  selector: '[prosemirrorRender]',
  providers: [ProsemirrorService, StateUpdated$, EditorView$, OVERLAY_PROVIDERS],
  exportAs: 'prosemirrorRender',
  standalone: true,
})
export class RenderDirective<C extends IProsemirrorConfig> implements OnChanges, OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly prosemirrorService = inject<ProsemirrorService<C>>(ProsemirrorService);

  @Input('prosemirrorRender') public state?: object | null;
  @Input('prosemirrorRenderConfig') public config: new (...args: any[]) => C;

  public prosemirrorEvents = output<PluginsEvents<ConfigPlugins<C>>>();

  public publicApi$ = new BehaviorSubject<PluginsAPI<ConfigPlugins<C>> | null>(null);

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    const { eventStream$, publicApi } = this.prosemirrorService.createEditor(
      this.elementRef.nativeElement,
      this.config,
      {
        mode: EditorMode.View,
        initialState: <EditorJSONState>this.state,
      },
    );

    this.publicApi$.next(publicApi);
    eventStream$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(event => this.prosemirrorEvents.emit(event));
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { state } = changes;
    if (state && !state.firstChange) {
      this.prosemirrorService.updateState(<EditorJSONState>this.state);
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
